import type { FC, ReactNode, Ref } from 'react'
import type { JourneyFrame as JourneyFrameData } from '@/types'

import { cn } from '@/lib/utils'
import InstrumentArt from '@/components/landing/instrument-art'

interface JourneyFrameProps {
    frame: JourneyFrameData
    artRef?: Ref<HTMLDivElement>
    className?: string
}

const ART_WIDTH: Record<string, string> = {
    mic: 'w-[28%]',
    amp: 'w-[62%]',
    sequencer: 'w-[78%]',
    'les-paul': 'w-[26%]',
    telecaster: 'w-[26%]',
    'drum-kit': 'w-[76%]',
    bass: 'w-[22%]',
    crest: 'w-[56%]'
}

const Sprockets: FC = (): ReactNode => (
    <span
        aria-hidden='true'
        className='block h-3 w-full opacity-60'
        style={{
            backgroundImage: 'linear-gradient(90deg, #C9D3E3 0 10px, transparent 10px 24px)',
            backgroundSize: '24px 100%'
        }}
    />
)

// One frame of the journey film strip. A live photo slots in here once supplied;
// until then the frame carries the cut-paper art for that part of the story.
const JourneyFrame: FC<JourneyFrameProps> = ({ frame, artRef, className }): ReactNode => (
    <article className={cn('relative flex shrink-0 flex-col bg-night-deep p-3 ring-1 ring-paper/10', className)} aria-labelledby={`journey-${frame.id}`}>
        <Sprockets />

        <div className='relative my-3 flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-night'>
            <span aria-hidden='true' className='display-type absolute -left-2 top-1 text-[clamp(4rem,9vw,8rem)] text-paper/[0.06]'>{frame.number}</span>

            {frame.photo ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={frame.photo.src} alt={frame.photo.alt} className='absolute inset-0 h-full w-full object-cover grayscale' />
            ) : (
                <div ref={artRef} className={cn('relative will-change-transform', ART_WIDTH[frame.art])}>
                    <InstrumentArt kind={frame.art} animated={frame.art === 'sequencer'} />
                </div>
            )}
        </div>

        <div className='px-1 pb-1'>
            <p className='mono-label text-[10px] text-night-text'>Frame {frame.number}</p>
            <h3 id={`journey-${frame.id}`} className='display-type mt-1.5 text-[clamp(1.6rem,2.4vw,2.4rem)] text-paper'>{frame.title}</h3>
            <p className='mt-2 max-w-[40ch] text-[15px] leading-[1.55] text-night-text'>{frame.caption}</p>
        </div>

        <Sprockets />
    </article>
)

export default JourneyFrame
